import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

// Define available languages
export type Language = 'en' | 'fr' | 'pidgin';

// Define translations interface
interface TranslationSet {
  [key: string]: string;
}

interface PageTranslations {
  common: TranslationSet;
  dashboard: TranslationSet;
  detect: TranslationSet;
  sources: TranslationSet;
  report: TranslationSet;
  learn: TranslationSet;
  tools: TranslationSet;
  community: TranslationSet;
  privacy: TranslationSet;
  voiceAgent: TranslationSet;
  auth: TranslationSet;
  index: TranslationSet;
}

interface Translations {
  [key: string]: PageTranslations;
}

// Create translations for the app
const translations: Translations = {
  en: {
    dashboard: 'Dashboard',
    analyzeContent: 'Analyze Content',
    sourceCredibility: 'Source Credibility',
    reportMisinfo: 'Report Misinfo',
    mediaLiteracy: 'Media Literacy',
    osintTools: 'OSINT Tools',
    community: 'Community',
    privacySecurity: 'Privacy & Security',
    voiceAgent: 'Voice Agent',
    signOut: 'Sign Out',
    welcomeBack: 'Welcome back',
    newAnalysis: 'New Analysis',
    globalMisinfoTracker: 'Global Misinformation Tracker',
    realTimeVisualization: 'Real-time visualization of misinformation spread across the globe',
    analysesRun: 'Analyses Run',
    misinformationDetected: 'Misinformation Detected',
    sourcesVerified: 'Sources Verified',
    reportsSubmitted: 'Reports Submitted',
    mainFeatures: 'Main Features',
    analyzeContentDesc: 'AI-powered analysis of text, images, and videos to detect misinformation patterns',
    sourceCredibilityDesc: 'Comprehensive verification of news sources and fact-checking of claims',
    reportMisinfoDesc: 'Submit and track suspicious content for community verification',
    mediaLiteracyDesc: 'Educational resources and guides for critical thinking and fact-checking',
    launchTool: 'Launch Tool',
    recentActivity: 'Recent Activity',
    textAnalysis: 'Text Analysis',
    sourceCheck: 'Source Check',
    imageAnalysis: 'Image Analysis',
    factual: 'Factual',
    misleading: 'Misleading',
    manipulated: 'Manipulated',
    lowCredibility: 'Low Credibility',
    combatingMisinfo: 'Combating misinformation with technology',
    language: 'Language',
  },
  fr: {
    dashboard: 'Tableau de Bord',
    analyzeContent: 'Analyser le Contenu',
    sourceCredibility: 'Crédibilité des Sources',
    reportMisinfo: 'Signaler Désinformation',
    mediaLiteracy: 'Littératie Médiatique',
    osintTools: 'Outils OSINT',
    community: 'Communauté',
    privacySecurity: 'Confidentialité & Sécurité',
    voiceAgent: 'Agent Vocal',
    signOut: 'Déconnexion',
    welcomeBack: 'Bon retour',
    newAnalysis: 'Nouvelle Analyse',
    globalMisinfoTracker: 'Suivi Global de la Désinformation',
    realTimeVisualization: 'Visualisation en temps réel de la propagation de la désinformation à travers le monde',
    analysesRun: 'Analyses Effectuées',
    misinformationDetected: 'Désinformation Détectée',
    sourcesVerified: 'Sources Vérifiées',
    reportsSubmitted: 'Rapports Soumis',
    mainFeatures: 'Fonctionnalités Principales',
    analyzeContentDesc: 'Analyse basée sur l\'IA de textes, images et vidéos pour détecter les modèles de désinformation',
    sourceCredibilityDesc: 'Vérification complète des sources d\'information et vérification des faits',
    reportMisinfoDesc: 'Soumettre et suivre des contenus suspects pour vérification par la communauté',
    mediaLiteracyDesc: 'Ressources éducatives et guides pour la pensée critique et la vérification des faits',
    launchTool: 'Lancer l\'Outil',
    recentActivity: 'Activité Récente',
    textAnalysis: 'Analyse de Texte',
    sourceCheck: 'Vérification de Source',
    imageAnalysis: 'Analyse d\'Image',
    factual: 'Factuel',
    misleading: 'Trompeur',
    manipulated: 'Manipulé',
    lowCredibility: 'Faible Crédibilité',
    combatingMisinfo: 'Combattre la désinformation avec la technologie',
    language: 'Langue',
  },
  pidgin: {
    dashboard: 'Dashboard',
    analyzeContent: 'Check Content',
    sourceCredibility: 'Source Trust Level',
    reportMisinfo: 'Report Fake News',
    mediaLiteracy: 'Media Education',
    osintTools: 'OSINT Tools Dem',
    community: 'Our People',
    privacySecurity: 'Privacy & Security',
    voiceAgent: 'Voice Agent',
    signOut: 'Comot',
    welcomeBack: 'Welcome back',
    newAnalysis: 'New Analysis',
    globalMisinfoTracker: 'World Fake News Tracker',
    realTimeVisualization: 'Live show for how fake news dey spread for world',
    analysesRun: 'Analysis Wey Don Run',
    misinformationDetected: 'Fake News Wey Dey Catch',
    sourcesVerified: 'Source Wey Dey Verified',
    reportsSubmitted: 'Reports Wey Don Submit',
    mainFeatures: 'Main-Main Features',
    analyzeContentDesc: 'AI dey help analyze text, pictures, and videos to find fake news pattern',
    sourceCredibilityDesc: 'Full check of news sources and fact-checking of wetin dem talk',
    reportMisinfoDesc: 'Submit and track suspicious content make community verify am',
    mediaLiteracyDesc: 'Learning materials and guides for better thinking and fact-checking',
    launchTool: 'Open Tool',
    recentActivity: 'Wetin Don Happen Recently',
    textAnalysis: 'Text Analysis',
    sourceCheck: 'Source Check',
    imageAnalysis: 'Picture Analysis',
    factual: 'True True',
    misleading: 'E Dey Mislead',
    manipulated: 'Dem Don Edit Am',
    lowCredibility: 'E No Reliable',
    combatingMisinfo: 'We dey fight fake news with technology',
    language: 'Language',
  },
};

// Create the context
interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string) => string;
}

export const LanguageContext = createContext<LanguageContextType>({
  language: 'en',
  setLanguage: () => {},
  t: () => '',
});

// Provider component
interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider: React.FC<LanguageProviderProps> = ({ children }) => {
  // Initialize language from localStorage or default to 'en'
  const [language, setLanguage] = useState<Language>(() => {
    const savedLanguage = localStorage.getItem('language');
    return (savedLanguage as Language) || 'en';
  });

  // Update localStorage when language changes
  useEffect(() => {
    localStorage.setItem('language', language);
  }, [language]);

  // Translation function
  const t = (key: string): string => {
    return translations[language]?.[key] || translations['en'][key] || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

// Custom hook for using the language context
export const useLanguage = () => useContext(LanguageContext);
